import { memo } from 'react'
import { Card } from '@/components/ui/Card'
import { 
  Users, 
  Clock, 
  CheckCircle, 
  Target,
  Scale,
  Zap
} from 'lucide-react'
import type { TeamMetrics } from '@/lib/jira/metrics'

interface StatCardProps {
  label: string
  value: string | number
  icon: React.ReactNode
  bgColor: string
  iconColor: string
  trend?: {
    value: number
    isPositive: boolean
  }
  description?: string
}

const StatCard = memo(function StatCard({
  label,
  value,
  icon,
  bgColor,
  iconColor,
  trend,
  description,
}: StatCardProps) {
  return (
    <Card variant="compact" padding="sm" className="relative overflow-hidden">
      <div className="flex items-start gap-3">
        <div className={`p-2.5 rounded-xl ${bgColor}`}>
          <div className={iconColor}>{icon}</div>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">{label}</p>
          <div className="flex items-baseline gap-2">
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
            {trend && (
              <span className={`text-xs font-medium ${trend.isPositive ? 'text-green-500' : 'text-red-500'}`}>
                {trend.isPositive ? '+' : ''}{trend.value}%
              </span>
            )}
          </div>
          {description && (
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{description}</p>
          )}
        </div>
      </div>
      {/* Dekoratif arka plan */}
      <div className={`absolute -right-4 -bottom-4 w-20 h-20 rounded-full ${bgColor} opacity-20`} />
    </Card>
  )
})

interface JiraPerformanceStatsProps {
  metrics: TeamMetrics
  loading?: boolean
  className?: string
}

const LoadingState = memo(function LoadingState() {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
      {[...Array(6)].map((_, i) => (
        <div key={i} className="h-24 bg-gray-100 dark:bg-gray-800 rounded-xl animate-pulse" />
      ))}
    </div>
  )
})

const JiraPerformanceStats = memo(function JiraPerformanceStats({
  metrics,
  loading = false,
  className = '',
}: JiraPerformanceStatsProps) {
  if (loading) {
    return <LoadingState />
  }

  const stats: StatCardProps[] = [
    {
      label: 'Takım Üyesi',
      value: metrics.totalMembers,
      icon: <Users className="h-5 w-5" />,
      bgColor: 'bg-blue-100 dark:bg-blue-900/30',
      iconColor: 'text-blue-600 dark:text-blue-400',
      description: 'Aktif üye sayısı',
    },
    {
      label: 'Açık Görev',
      value: metrics.totalOpenTasks,
      icon: <Clock className="h-5 w-5" />,
      bgColor: 'bg-amber-100 dark:bg-amber-900/30',
      iconColor: 'text-amber-600 dark:text-amber-400',
      description: 'Devam eden işler',
    },
    {
      label: 'Tamamlanan (30g)',
      value: metrics.totalCompletedTasks,
      icon: <CheckCircle className="h-5 w-5" />,
      bgColor: 'bg-green-100 dark:bg-green-900/30',
      iconColor: 'text-green-600 dark:text-green-400',
      description: 'Son 30 günde',
    },
    {
      label: 'Tamamlama Oranı',
      value: `${metrics.avgCompletionRate}%`,
      icon: <Target className="h-5 w-5" />,
      bgColor: 'bg-purple-100 dark:bg-purple-900/30',
      iconColor: 'text-purple-600 dark:text-purple-400',
      trend: {
        value: metrics.avgCompletionRate > 70 ? 5 : -3,
        isPositive: metrics.avgCompletionRate > 70,
      },
    },
    {
      label: 'Haftalık Velocity',
      value: metrics.teamVelocity,
      icon: <Zap className="h-5 w-5" />,
      bgColor: 'bg-indigo-100 dark:bg-indigo-900/30',
      iconColor: 'text-indigo-600 dark:text-indigo-400',
      description: 'görev/hafta',
    },
    {
      label: 'İş Yükü Dengesi',
      value: `${metrics.workloadBalance}%`,
      icon: <Scale className="h-5 w-5" />,
      bgColor: metrics.workloadBalance >= 70 
        ? 'bg-emerald-100 dark:bg-emerald-900/30' 
        : 'bg-orange-100 dark:bg-orange-900/30',
      iconColor: metrics.workloadBalance >= 70 
        ? 'text-emerald-600 dark:text-emerald-400'
        : 'text-orange-600 dark:text-orange-400',
    },
  ]

  return (
    <div className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 ${className}`}>
      {stats.map((stat) => (
        <StatCard key={stat.label} {...stat} />
      ))}
    </div>
  )
})

// Tek satırlık özet istatistikler
interface JiraPerformanceSummaryProps {
  metrics: TeamMetrics
  loading?: boolean
  className?: string
}

export const JiraPerformanceSummary = memo(function JiraPerformanceSummary({
  metrics,
  loading = false,
  className = '',
}: JiraPerformanceSummaryProps) {
  if (loading) {
    return (
      <div className="flex items-center gap-6">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-8 w-24 bg-gray-100 dark:bg-gray-800 rounded animate-pulse" />
        ))}
      </div>
    )
  }

  return (
    <div className={`flex flex-wrap items-center gap-6 ${className}`}>
      <div className="flex items-center gap-2">
        <Users className="h-4 w-4 text-blue-500" />
        <span className="text-sm text-gray-600 dark:text-gray-400">
          <span className="font-semibold text-gray-900 dark:text-white">{metrics.totalMembers}</span> üye
        </span>
      </div>
      
      <div className="flex items-center gap-2">
        <Clock className="h-4 w-4 text-amber-500" />
        <span className="text-sm text-gray-600 dark:text-gray-400">
          <span className="font-semibold text-gray-900 dark:text-white">{metrics.totalOpenTasks}</span> açık görev
        </span>
      </div>
      
      <div className="flex items-center gap-2">
        <Target className="h-4 w-4 text-purple-500" />
        <span className="text-sm text-gray-600 dark:text-gray-400">
          <span className="font-semibold text-gray-900 dark:text-white">%{metrics.avgCompletionRate}</span> tamamlama
        </span>
      </div>
      
      <div className="flex items-center gap-2">
        <Zap className="h-4 w-4 text-indigo-500" />
        <span className="text-sm text-gray-600 dark:text-gray-400">
          <span className="font-semibold text-gray-900 dark:text-white">{metrics.teamVelocity}</span> görev/hafta
        </span>
      </div>
    </div>
  )
})

export default JiraPerformanceStats

